export function groupAndAverageTasks(assessmentResultsByStudent) {
  // Add defensive check for input parameter
  if (!assessmentResultsByStudent || !Array.isArray(assessmentResultsByStudent)) {
    console.warn('groupAndAverageTasks received non-array input:', assessmentResultsByStudent);
    return [];
  }

  return assessmentResultsByStudent.map((student) => {
    // Additional safety check for student object
    if (!student || typeof student !== 'object') {
      console.warn('groupAndAverageTasks received invalid student object:', student);
      return { tasks: [] };
    }

    if (!student.tasks || !Array.isArray(student.tasks) || student.tasks.length === 0) {
      return {
        ...student,
        tasks: [],
      };
    }

    const groupedData = {};

    student.tasks.forEach((task) => {
      // Safety check for task object
      if (!task || typeof task !== 'object') {
        console.warn('groupAndAverageTasks encountered invalid task:', task);
        return;
      }

      // Use parentTaskid as group key; fallback to task.name or task.id
      const groupKey = task.parentTaskid || task.name || task.id || 'unknown';

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          name: task.parentTaskName || task.name || 'Unknown Task', // Use Parent Task Name or fallback to task.name
          count: 0,
          totalScore: 0,
          totalZScore: 0,
          totalStandardScore: 0,
          totalSTenScore: 0,
          totalGroupPercentile: 0,
          totalGlobalPercentile: 0,
          totalOrder: 0,
          answerids: [], // Collect answerids
        };
      }

      groupedData[groupKey].count++;
      groupedData[groupKey].totalScore += task.score || 0;
      groupedData[groupKey].totalZScore += task.zScore || 0;
      groupedData[groupKey].totalStandardScore += task.standardScore || 0;
      groupedData[groupKey].totalSTenScore += task.sTenScore || 0;
      groupedData[groupKey].totalGroupPercentile += task.groupPercentile || 0;
      groupedData[groupKey].totalGlobalPercentile += task.globalPercentile || 0;
      groupedData[groupKey].totalOrder += task.order || 0;
      if (task.answerid !== undefined) {
        groupedData[groupKey].answerids.push(task.answerid);
      }
    });

    const groupedTasks = Object.values(groupedData)
      .map((group) => ({
        name: group.name,
        score: group.count > 0 ? group.totalScore / group.count : 0,
        zScore: group.count > 0 ? group.totalZScore / group.count : 0,
        standardScore: group.count > 0 ? group.totalStandardScore / group.count : 0,
        sTenScore: group.count > 0 ? group.totalSTenScore / group.count : 0,
        groupPercentile: group.count > 0 ? group.totalGroupPercentile / group.count : 0,
        globalPercentile: group.count > 0 ? group.totalGlobalPercentile / group.count : 0,
        order: group.count > 0 ? group.totalOrder / group.count : 0, // Average order
        answerid:
          group.answerids.length === 1 ? group.answerids[0] : group.answerids, // Array if multiple, single if one
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    return {
      ...student,
      tasks: groupedTasks,
    };
  });
}

export function groupClassResultsforExport(classesResults) {
  // Add defensive check for input parameter
  if (!classesResults || typeof classesResults !== 'object') {
    console.warn('groupClassResultsforExport received invalid input:', classesResults);
    return {};
  }

  return Object.entries(classesResults).reduce((acc, [classId, classObj]) => {
    // Safety check for classObj
    if (!classObj || typeof classObj !== 'object') {
      console.warn('groupClassResultsforExport encountered invalid class object:', classObj);
      return acc;
    }

    acc[classId] = {
      name: classObj.name || 'Unknown Class',
      students: {},
    };

    // Safety check for students object
    if (!classObj.students || typeof classObj.students !== 'object') {
      console.warn('groupClassResultsforExport: class has no valid students object:', classId);
      return acc;
    }

    Object.entries(classObj.students).forEach(([studentId, student]) => {
      // Safety check for student object
      if (!student || typeof student !== 'object') {
        console.warn('groupClassResultsforExport encountered invalid student:', student);
        acc[classId].students[studentId] = { results: [] };
        return;
      }

      if (!student.results || !Array.isArray(student.results) || student.results.length === 0) {
        acc[classId].students[studentId] = { ...student, results: [] }; // Keep empty results as is
        return;
      }

      acc[classId].students[studentId] = {
        ...student,
        results: student.results.map((result) => {
          // Safety check for result object
          if (!result || typeof result !== 'object') {
            console.warn('groupClassResultsforExport encountered invalid result:', result);
            return { ...result, groupedTasks: [], originalTasks: [] };
          }

          const groupedTasks = {};

          // Safety check for tasks array
          if (!result.tasks || !Array.isArray(result.tasks)) {
            return {
              ...result,
              groupedTasks: [],
              originalTasks: result.tasks || [],
            };
          }

          // Group tasks by parentTaskId OR individual task if no parent
          result.tasks.forEach((task) => {
            // Safety check for task object
            if (!task || typeof task !== 'object') {
              console.warn('groupClassResultsforExport encountered invalid task:', task);
              return;
            }

            const groupKey = task.parentTaskid
              ? `group-${task.parentTaskid}`
              : `task-${task.taskid || task.id || 'unknown'}`;

            if (!groupedTasks[groupKey]) {
              groupedTasks[groupKey] = {
                name: task.parentTaskName || task.name || 'Unknown Task', // Use parent name if exists
                tasks: [],
                aggregated: {
                  rawScore: 0,
                  standardScore: 0,
                  count: 0,
                },
                answerids: [],
              };
            }

            // Add task to group
            groupedTasks[groupKey].tasks.push(task);
            groupedTasks[groupKey].aggregated.rawScore += task.score || 0;
            groupedTasks[groupKey].aggregated.standardScore += task.standardScore || 0;
            groupedTasks[groupKey].aggregated.count++;
            if (task.answerid !== undefined) {
              groupedTasks[groupKey].answerids.push(task.answerid);
            }
          });

          // Calculate averages for grouped tasks
          Object.values(groupedTasks).forEach((group) => {
            if (group.aggregated.count > 0) {
              group.aggregated.rawScore = Math.round(
                (group.aggregated.rawScore / group.aggregated.count) * 100
              );
              group.aggregated.standardScore = Math.round(
                group.aggregated.standardScore / group.aggregated.count
              );
            }
          });

          return {
            ...result,
            groupedTasks: Object.values(groupedTasks).map((group) => ({
              ...group,
              answerid:
                group.answerids.length === 1
                  ? group.answerids[0]
                  : group.answerids,
            })),
            originalTasks: result.tasks, // Preserve original tasks
          };
        }),
      };
    });

    return acc;
  }, {});
}

export function groupClassResults(classesResults) {
  // Add defensive check for input parameter
  if (!classesResults || typeof classesResults !== 'object') {
    console.warn('groupClassResults received invalid input:', classesResults);
    return {};
  }

  // Maintain the top-level class structure
  return Object.entries(classesResults).reduce((acc, [classId, classObj]) => {
    // Safety check for classObj
    if (!classObj || typeof classObj !== 'object') {
      console.warn('groupClassResults encountered invalid class object:', classObj);
      return acc;
    }

    acc[classId] = {
      name: classObj.name || 'Unknown Class',
      students: {},
    };

    // Safety check for students object
    if (!classObj.students || typeof classObj.students !== 'object') {
      console.warn('groupClassResults: class has no valid students object:', classId);
      return acc;
    }

    // Process each student's results
    Object.entries(classObj.students).forEach(([studentId, student]) => {
      // Safety check for student object
      if (!student || typeof student !== 'object') {
        console.warn('groupClassResults encountered invalid student:', student);
        acc[classId].students[studentId] = { results: [] };
        return;
      }

      if (!student.results || !Array.isArray(student.results) || student.results.length === 0) {
        acc[classId].students[studentId] = { ...student, results: [] }; // Keep empty results as is
        return;
      }

      // Process each assessment result
      acc[classId].students[studentId] = {
        ...student,
        results: student.results.map((result) => {
          // Safety check for result object
          if (!result || typeof result !== 'object') {
            console.warn('groupClassResults encountered invalid result:', result);
            return { ...result, groupedTasks: [], originalTasks: [] };
          }

          const groupedTasks = {};

          // Safety check for tasks array
          if (!result.tasks || !Array.isArray(result.tasks)) {
            return {
              ...result,
              groupedTasks: [],
              originalTasks: result.tasks || [],
            };
          }

          // Group tasks by parent task
          result.tasks.forEach((task) => {
            // Safety check for task object
            if (!task || typeof task !== 'object') {
              console.warn('groupClassResults encountered invalid task:', task);
              return;
            }

            const groupKey = task.parentTaskid
              ? `group-${task.parentTaskid}`
              : `task-${task.name || task.id || 'unknown'}`;

            if (!groupedTasks[groupKey]) {
              groupedTasks[groupKey] = {
                name: task.parentTaskName || task.name || 'Unknown Task',
                tasks: [],
                aggregated: {
                  score: 0,
                  zScore: 0,
                  standardScore: 0,
                  sTenScore: 0,
                  duration: 0,
                  count: 0,
                },
                answerids: [],
              };
            }

            // Add task to group
            groupedTasks[groupKey].tasks.push(task);
            groupedTasks[groupKey].aggregated.score += task.score || 0;
            groupedTasks[groupKey].aggregated.zScore += task.zScore || 0;
            groupedTasks[groupKey].aggregated.standardScore += task.standardScore || 0;
            groupedTasks[groupKey].aggregated.sTenScore += task.sTenScore || 0;
            groupedTasks[groupKey].aggregated.duration += task.duration || 0;
            groupedTasks[groupKey].aggregated.count++;
            if (task.answerid !== undefined) {
              groupedTasks[groupKey].answerids.push(task.answerid);
            }
          });

          // Calculate averages
          Object.values(groupedTasks).forEach((group) => {
            const { aggregated } = group;
            if (aggregated.count > 0) {
              aggregated.score /= aggregated.count;
              aggregated.zScore /= aggregated.count;
              aggregated.standardScore /= aggregated.count;
              aggregated.sTenScore /= aggregated.count;
              // Duration remains as total
            }
          });

          return {
            ...result,
            groupedTasks: Object.values(groupedTasks).map((group) => ({
              ...group,
              answerid:
                group.answerids.length === 1
                  ? group.answerids[0]
                  : group.answerids,
            })),
            originalTasks: result.tasks, // Preserve original tasks
          };
        }),
      };
    });

    return acc;
  }, {});
}

export function preprocessStudentResults(individualStudentResult) {
  // Add defensive check to ensure input is an array
  if (!individualStudentResult || !Array.isArray(individualStudentResult)) {
    console.warn('preprocessStudentResults received non-array input:', individualStudentResult);
    return []; // Return empty array instead of crashing
  }

  return individualStudentResult.map((assessment) => {
    // Safety check for assessment object
    if (!assessment || typeof assessment !== 'object') {
      console.warn('preprocessStudentResults encountered invalid assessment:', assessment);
      return {
        ...assessment,
        groupedTasks: [],
      };
    }

    const groupedTasks = {};

    // Safety check for tasks array
    if (!assessment.tasks || !Array.isArray(assessment.tasks)) {
      console.warn('preprocessStudentResults: assessment has no valid tasks array:', assessment);
      return {
        ...assessment,
        groupedTasks: [],
      };
    }

    assessment.tasks.forEach((task) => {
      // Safety check for task object
      if (!task || typeof task !== 'object') {
        console.warn('preprocessStudentResults encountered invalid task:', task);
        return;
      }

      let groupKey;

      // If parentTaskid is null, group based on task name; otherwise, use parentTaskid
      if (task.parentTaskid) {
        groupKey = `group-${task.parentTaskid}`;
      } else {
        groupKey = `task-${task.name || task.id || 'unknown'}`; // Use task name for standalone tasks
      }

      if (!groupedTasks[groupKey]) {
        groupedTasks[groupKey] = {
          name: task.parentTaskid ? (task.parentTaskName || 'Unknown Parent Task') : (task.name || 'Unknown Task'), // Use Parent Task Name or task.name
          aggregated: {
            score: 0,
            zScore: 0,
            standardScore: 0,
            sTenScore: 0,
            duration: 0,
            count: 0,
          },
          tasks: [], // Store individual tasks
          answerids: [],
        };
      }

      // Add task to the group
      groupedTasks[groupKey].tasks.push(task);
      groupedTasks[groupKey].aggregated.score += task.score || 0;
      groupedTasks[groupKey].aggregated.zScore += task.zScore || 0;
      groupedTasks[groupKey].aggregated.standardScore += task.standardScore || 0;
      groupedTasks[groupKey].aggregated.sTenScore += task.sTenScore || 0;
      groupedTasks[groupKey].aggregated.duration += task.duration || 0;
      groupedTasks[groupKey].aggregated.count++;
      if (task.answerid !== undefined) {
        groupedTasks[groupKey].answerids.push(task.answerid);
      }
    });

    // Calculate averages for the aggregated data
    Object.values(groupedTasks).forEach((group) => {
      const { aggregated } = group;
      if (aggregated.count > 0) {
        aggregated.score /= aggregated.count;
        aggregated.zScore /= aggregated.count;
        aggregated.standardScore /= aggregated.count;
        aggregated.sTenScore /= aggregated.count;
        // Duration remains as the total time
      }
    });

    return {
      ...assessment,
      groupedTasks: Object.values(groupedTasks).map((group) => ({
        ...group,
        answerid:
          group.answerids.length === 1 ? group.answerids[0] : group.answerids,
      })),
    };
  });
}